import { supabase } from '../lib/supabase';

class ConsultationService {
  static async getConsultations(options = {}) {
    try {
      console.log('Fetching consultations from Supabase with options:', options);
      
      let query = supabase
        .from('consultation_submissions')
        .select('*');
      
      // Apply filters if provided
      if (options.quality) {
        query = query.eq('quality', options.quality);
      }
      
      if (options.status) {
        query = query.eq('status', options.status);
      }
      
      if (options.membership) {
        query = query.eq('membership', options.membership);
      }
      
      if (options.campaign) {
        query = query.eq('which_campaign', options.campaign);
      }
      
      if (options.search) {
        query = query.or(`name.ilike.%${options.search}%,last_name.ilike.%${options.search}%,email.ilike.%${options.search}%,phone.ilike.%${options.search}%`);
      }
      
      if (options.dateFrom) {
        query = query.gte('date', options.dateFrom);
      }
      
      if (options.dateTo) {
        query = query.lte('date', options.dateTo);
      }
      
      // Apply sorting - default to newest first
      const sortField = options.sortField || 'date';
      const sortOrder = options.sortOrder || { ascending: false };
      query = query.order(sortField, sortOrder);
      
      // Apply pagination
      const limit = options.limit || 100;
      const from = options.from || 0;
      const to = from + limit - 1;
      query = query.range(from, to);
      
      const { data, error } = await query;
      
      if (error) {
        console.error('Error fetching consultations from Supabase:', error);
        throw error;
      }
      
      return data || [];
    } catch (error) {
      console.error('Error in getConsultations:', error);
      throw error;
    }
  }

  static async updateConsultation(id, updateData) {
    try {
      console.log('Updating consultation:', id, updateData);
      
      const { data, error } = await supabase
        .from('consultation_submissions')
        .update({
          ...updateData,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select();
      
      if (error) {
        console.error('Error updating consultation:', error);
        throw error;
      }
      
      console.log('Successfully updated consultation:', data);
      return data[0];
    } catch (error) {
      console.error('Error in updateConsultation:', error);
      throw error;
    }
  }

  static async createConsultation(consultationData) {
    try {
      console.log('Creating consultation:', consultationData);
      
      const { data, error } = await supabase
        .from('consultation_submissions')
        .insert([{
          date: consultationData.date || new Date().toISOString(),
          name: consultationData.name,
          last_name: consultationData.last_name,
          phone: consultationData.phone,
          email: consultationData.email,
          quality: consultationData.quality || 'Qualified',
          status: consultationData.status || 'No Answer',
          membership: consultationData.membership || '',
          followup: consultationData.followup || '',
          which_campaign: consultationData.which_campaign || 'Website Form'
        }])
        .select();
      
      if (error) {
        console.error('Error creating consultation:', error);
        throw error;
      }
      
      console.log('Successfully created consultation:', data);
      return data[0];
    } catch (error) {
      console.error('Error in createConsultation:', error);
      throw error;
    }
  }

  static async deleteConsultation(id) {
    try {
      console.log('Deleting consultation:', id);
      
      const { error } = await supabase
        .from('consultation_submissions')
        .delete()
        .eq('id', id);
      
      if (error) {
        console.error('Error deleting consultation:', error);
        throw error;
      }
      
      console.log('Successfully deleted consultation:', id);
      return true;
    } catch (error) {
      console.error('Error in deleteConsultation:', error);
      throw error;
    }
  }

  static async getConsultationStats() {
    try {
      console.log('Fetching consultation statistics...');
      
      const { data, error } = await supabase
        .from('consultation_submissions')
        .select('quality, status, membership, which_campaign');
      
      if (error) {
        console.error('Error fetching consultation stats:', error);
        throw error;
      }
      
      // Calculate statistics
      const stats = {
        total: data.length,
        byQuality: {},
        byStatus: {},
        byMembership: {},
        byCampaign: {}
      };
      
      data.forEach(item => {
        // Quality stats
        stats.byQuality[item.quality] = (stats.byQuality[item.quality] || 0) + 1;
        
        // Status stats
        stats.byStatus[item.status] = (stats.byStatus[item.status] || 0) + 1;
        
        // Membership stats
        if (item.membership) {
          stats.byMembership[item.membership] = (stats.byMembership[item.membership] || 0) + 1;
        }
        
        // Campaign stats
        stats.byCampaign[item.which_campaign] = (stats.byCampaign[item.which_campaign] || 0) + 1;
      });
      
      return stats;
    } catch (error) {
      console.error('Error in getConsultationStats:', error);
      return {
        total: 0,
        byQuality: {},
        byStatus: {},
        byMembership: {},
        byCampaign: {}
      };
    }
  }

  static async bulkUpdateConsultations(ids, updateData) {
    try {
      console.log('Bulk updating consultations:', ids, updateData);
      
      const { data, error } = await supabase
        .from('consultation_submissions')
        .update({
          ...updateData,
          updated_at: new Date().toISOString()
        })
        .in('id', ids)
        .select();
      
      if (error) {
        console.error('Error bulk updating consultations:', error);
        throw error;
      }
      
      console.log('Successfully bulk updated consultations:', data);
      return data;
    } catch (error) {
      console.error('Error in bulkUpdateConsultations:', error);
      throw error;
    }
  }

  static async exportConsultations(options = {}) {
    try {
      console.log('Exporting consultations with options:', options);
      
      // Get all data without pagination for export
      const exportOptions = { ...options, limit: 10000, from: 0 };
      const data = await this.getConsultations(exportOptions);
      
      // Convert to CSV format
      if (data.length === 0) {
        return { csv: '', filename: 'consultations_empty.csv' };
      }
      
      const headers = ['Date', 'Name', 'Last Name', 'Phone', 'Email', 'Quality', 'Status', 'Membership', 'Followup', 'Campaign'];
      const csvContent = [
        headers.join(','),
        ...data.map(row => [
          new Date(row.date).toLocaleDateString(),
          `"${row.name}"`,
          `"${row.last_name}"`,
          `"${row.phone}"`,
          `"${row.email}"`,
          `"${row.quality}"`,
          `"${row.status}"`,
          `"${row.membership || ''}"`,
          `"${row.followup || ''}"`,
          `"${row.which_campaign || ''}"`
        ].join(','))
      ].join('\n');
      
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `consultations_${timestamp}.csv`;
      
      return { csv: csvContent, filename };
    } catch (error) {
      console.error('Error in exportConsultations:', error);
      throw error;
    }
  }
}

export default ConsultationService; 